import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	FormControl,
	FormLabel,
	TextField,
	Checkbox,
	FormControlLabel,
	IconButton,
	Box,
	Button,
	Typography,
	Chip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DiamondIcon from '@mui/icons-material/Diamond';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';

const ShareToSocialMediaDialog = ({
	isOpen,
	onClose,
	message,
	setMessage,
	setScheduledDate,
	selectedAccounts,
	setSelectedAccounts,
	ayrshareProfileDetails,
	socialMediaIcons,
	formatAccountName,
	mediaFileValue,
	handleSocialMediaPopup,
	handlePost,
	setIsSetupDialog,
	intl,
	classes,
}) => {
	return (
		<Dialog open={isOpen} fullWidth maxWidth="sm" aria-labelledby="Share to Social Media">
			<DialogTitle className={classes.title}>
				{'Share on Social Media'}
				<IconButton aria-label="Close" className={classes.closeButton} onClick={onClose}>
					<CloseIcon />
				</IconButton>
			</DialogTitle>

			<DialogContent dividers sx={{ display: 'flex', flexWrap: 'wrap' }}>
				<Box sx={{ flex: 1, padding: 2 }}>
					<FormControl fullWidth sx={{ marginBottom: 2 }}>
						<FormLabel>Enter your post and #hashtags:</FormLabel>
						<TextField
							value={message}
							onChange={(e) => setMessage(e.target.value)}
							fullWidth
							multiline
							rows={4}
							variant="outlined"
						/>
					</FormControl>

					<FormControl fullWidth sx={{ marginBottom: 2 }}>
						<FormLabel>{'Set publication date'}</FormLabel>
						<DateTimePicker sx={{ marginLeft: 1, marginRight: 1 }} onChange={setScheduledDate} />
					</FormControl>

					<FormControl fullWidth sx={{ marginBottom: 2 }}>
						<FormLabel>{'Choose Social Accounts:'}</FormLabel>
						<Box sx={{ display: 'flex', flexDirection: 'column' }}>
							{ayrshareProfileDetails?.availableSocials.map((account) => {
								const isActive = ayrshareProfileDetails.activeSocials.includes(account);
								const Icon = socialMediaIcons[account];

								return (
									<FormControlLabel
										key={account}
										sx={{ marginBottom: 1, height: '24px' }}
										control={
											<Checkbox
												checked={selectedAccounts.includes(account)}
												disabled={!isActive}
												sx={{ color: isActive ? 'inherit' : 'gray' }}
												onChange={() =>
													setSelectedAccounts((prev) =>
														prev.includes(account)
															? prev.filter((a) => a !== account)
															: [...prev, account]
													)
												}
											/>
										}
										label={
											<Box sx={{ display: 'flex', alignItems: 'center', width: 'fit-content' }}>
												{isActive ? (
													<>
														<IconButton color="inherit" size="small">
															{Icon}
														</IconButton>
														<Typography
															sx={{
																color: 'inherit',
																textTransform: 'capitalize',
																fontSize: '0.875rem',
															}}
														>
															{formatAccountName(account)}
														</Typography>
													</>
												) : (
													<Chip
														label={
															<Box sx={{ display: 'flex', alignItems: 'center' }}>
																<Typography sx={{ color: 'white', fontSize: '0.875rem' }}>
																	{'Click to link'}
																</Typography>
																{Icon && (
																	<IconButton color="inherit" size="small">
																		{Icon}
																	</IconButton>
																)}
																<Typography
																	sx={{
																		color: 'white',
																		fontSize: '0.875rem',
																		marginLeft: Icon ? 0 : '4px',
																	}}
																>
																	{formatAccountName(account)}
																</Typography>
															</Box>
														}
														sx={{
															backgroundColor: '#999999',
															color: 'white',
															fontSize: '0.875rem',
															padding: '0',
															display: 'flex',
															alignItems: 'center',
															width: '100%',
															justifyContent: 'flex-start',
															height: '22px',
															'&:hover': { backgroundColor: '#999999', color: 'white' },
														}}
														onClick={() => {
															onClose();
															setIsSetupDialog(true);
														}}
													/>
												)}
											</Box>
										}
									/>
								);
							})}
						</Box>
					</FormControl>
				</Box>

				<Box sx={{ flex: 1, padding: 2 }}>
					{mediaFileValue ? (
						<img
							src={mediaFileValue.previewUrl}
							alt="Thumbnail Preview"
							style={{ width: '100%', height: 'auto', borderRadius: '8px' }}
						/>
					) : (
						<Box
							sx={{
								width: 200,
								height: 200,
								border: '2px solid',
								borderRadius: '8px',
								backgroundColor: '#f0f0f0',
							}}
						/>
					)}
					<Box
						sx={{
							padding: 2,
							marginTop: 2,
							backgroundColor: '#fff799',
							fontWeight: 'bold',
							borderRadius: '8px',
						}}
					>
						<span
							style={{
								backgroundColor: '#f89406',
								borderRadius: '3px',
								padding: '4px 8px 4px 0px',
							}}
						>
							<IconButton color="inherit" size="small">
								<DiamondIcon />
							</IconButton>
							{'Premium Feature'}
						</span>
						<p>
							{intl.formatMessage({
								id: 'pages.MediaFiles.components.ShareToSocialMedia.PremiumFeatureContent',
							})}
						</p>
					</Box>
				</Box>
			</DialogContent>

			<DialogActions sx={{ display: 'flex', justifyContent: 'space-between' }}>
				<Button
					onClick={handleSocialMediaPopup}
					variant="contained"
					color="primary"
					sx={{ marginRight: 'auto' }}
				>
					{'Config Social Media'}
				</Button>
				<Button onClick={onClose}>Cancel</Button>
				<Button variant="contained" color="primary" onClick={handlePost}>
					{'Post'}
				</Button>
			</DialogActions>
		</Dialog>
	);
};

// export default ShareToSocialMediaDialog;
ShareToSocialMediaDialog.propTypes = {
	isOpen: PropTypes.bool.isRequired,
	onClose: PropTypes.func.isRequired,
	message: PropTypes.string.isRequired,
	setMessage: PropTypes.func.isRequired,
	scheduledDate: PropTypes.object,
	setScheduledDate: PropTypes.func.isRequired,
	selectedAccounts: PropTypes.array.isRequired,
	setSelectedAccounts: PropTypes.func.isRequired,
	socialMediaIcons: PropTypes.object.isRequired,
	ayrshareProfileDetails: PropTypes.object,
	formatAccountName: PropTypes.func.isRequired,
	setIsSetupDialog: PropTypes.func.isRequired,
	handleSocialMediaPopup: PropTypes.func.isRequired,
	handlePost: PropTypes.func.isRequired,
	mediaFileValue: PropTypes.object,
	classes: PropTypes.object,
};

export default ShareToSocialMediaDialog;
